import { Map } from 'immutable';
import { createSelector } from 'reselect';

import { structureOptionsReducerKey } from './StructureOptionsConstants';

export function structureOptions(state) {
  return state.get(structureOptionsReducerKey);
}

export function initialOptions(state) {
  return structureOptions(state).get('initialOption');
}

export function ownOptions(state, ownProps) {
  return structureOptions(state).get(ownProps.id);
}

export function ownOptionsOrEmptyMap(state, ownProps) {
  const result = ownOptions(state, ownProps);

  if (!result) {
    return Map();
  }

  return result;
}

export function ownCurrentOptions(state, ownProps) {
  return ownOptionsOrEmptyMap(state, ownProps).get('current');
}

export function ownSavedOptions(state, ownProps) {
  return ownOptionsOrEmptyMap(state, ownProps).get('saved');
}

export function byGroup(state, ownProps) {
  return ownOptionsOrEmptyMap(state, ownProps).getIn(['saved', 'groups', 'by']);
}

export function byGroupCurrent(state, ownProps) {
  return ownOptionsOrEmptyMap(state, ownProps).getIn(['current', 'groups', 'by']);
}

export function moreGroup(state, ownProps) {
  return ownOptionsOrEmptyMap(state, ownProps).getIn(['saved', 'groups', 'more']);
}

export const isSimilarOptionChecked = createSelector([byGroupCurrent], (_byGroupCurrent) => {
  if (!_byGroupCurrent) {
    return false;
  }

  return Boolean(_byGroupCurrent.getIn(['options', 'Similar', 'checked']));
});

export const selectedTitleFromByGroup = createSelector([byGroup], (_byGroup) => {
  if (!_byGroup) {
    return null;
  }
  const selectedOption = _byGroup.get('options').find((item) => item.get('checked'));
  const subOptions = selectedOption.get('subOptions');

  if (subOptions) {
    return subOptions.find((item) => item.get('checked')).get('title');
  }

  return selectedOption.get('title');
});

export const inputsValuesFromMoreGroup = createSelector([moreGroup], (_moreGroup) => {
  if (!_moreGroup) {
    return null;
  }

  const optionsIds = _moreGroup.get('optionsIds');
  const options = _moreGroup.get('options');

  const inputsWithValues = optionsIds
    .map((id) => options.get(id))
    .filter((item) => {
      const isInput = item.get('type') === 'input';
      const inputValue = item.get('inputValue');

      return isInput && inputValue;
    });

  return inputsWithValues.map((item) => `${item.get('inputValue')} ${item.get('title')}`);
});

export const selectedOptionTitles = createSelector(
  [selectedTitleFromByGroup, inputsValuesFromMoreGroup],
  (_selectedTitleFromByGroup, _inputsValuesFromMoreGroup) => {
    if (!_selectedTitleFromByGroup || !_inputsValuesFromMoreGroup) {
      return null;
    }

    return [_selectedTitleFromByGroup, ..._inputsValuesFromMoreGroup];
  }
);

function concatOptions(res, group) {
  const options = group.get('options').valueSeq();

  return res ? res.concat(options) : options;
}

function onlyCheckedAndWithInputValue(option) {
  return option.get('checked') || option.get('inputValue');
}

function flattenSubOption(res, option) {
  const subOptions = option.get('subOptions');

  if (subOptions) {
    subOptions.filter(onlyCheckedAndWithInputValue).forEach((_option) => {
      res.push(_option);
    });

    return res;
  }

  res.push(option);

  return res;
}

function toNameValueObjectsArray(res, item) {
  res.push({
    name: item.get('name'),
    value: item.get('inputValue') || item.get('value'),
  });

  return res;
}

export const selectedOptionsNameValueObjectsArray = createSelector([ownSavedOptions], (_ownSavedOptions) => {
  if (!_ownSavedOptions) {
    return undefined;
  }

  const SimilarRadio = _ownSavedOptions.getIn(['groups', 'by', 'options', 'Similar']);

  if (SimilarRadio.get('checked')) {
    return [
      {
        name: SimilarRadio.get('name'),
        value: SimilarRadio.get('value'),
      },
    ];
  }

  const groups = _ownSavedOptions.get('groups');

  return groups
    .valueSeq()
    .reduce(concatOptions, null)
    .filter(onlyCheckedAndWithInputValue)
    .reduce(flattenSubOption, [])
    .reduce(toNameValueObjectsArray, []);
});

export const currentOptionsNameValueObjectsArray = createSelector([ownCurrentOptions], (_ownCurrentOptions) => {
  if (!_ownCurrentOptions) {
    return undefined;
  }

  const SimilarRadio = _ownCurrentOptions.getIn(['groups', 'by', 'options', 'Similar']);

  if (SimilarRadio.get('checked')) {
    return [
      {
        name: SimilarRadio.get('name'),
        value: SimilarRadio.get('value'),
      },
    ];
  }

  const groups = _ownCurrentOptions.get('groups');

  return groups
    .valueSeq()
    .reduce(concatOptions, null)
    .filter(onlyCheckedAndWithInputValue)
    .reduce(flattenSubOption, [])
    .reduce(toNameValueObjectsArray, []);
});
